import { digitsOnly } from './rows';

/*
 * The list filters run on AG Grid's own filter model. These helpers describe
 * the columns that carry each filter, the custom number-pattern options, and
 * how the toolbar's state maps to and from that model.
 */

// Value for rows that have no value for a field (e.g. no trunk).
export const NONE_VALUE = '__NONE__';

// Set filter value for numbers whose Overstamp is not the number itself.
export const OVERSTAMP_DIFFERS_VALUE = 'Yes';

// Grid columns that carry filters but aren't attribute filters.
export const FILTER_COLUMNS = {
    number: 'number',
    overstampDiffers: 'overstampDiffers',
    paste: 'pasteList',
};

export const EMPTY_FILTERS = {
    // Quick filter text; not part of the column filter model
    search: '',
    // Attribute filters: selected values, empty for all
    country: [],
    user: [],
    status: [],
    trunk: [],
    callingProfile: [],
    usage: [],
    overstampDiffers: false,
    // { mode, value, max, label } from the Number column's pattern filter
    regex: null,
    // { digits: string[], total } from Paste List
    paste: null,
};

// Each attribute filter is a set filter on the grid column with the same id.
export const ATTRIBUTE_FILTERS = [
    { key: 'country', field: 'countryName', chipLabel: 'Country' },
    { key: 'user', field: 'userAssigned', chipLabel: 'User' },
    {
        key: 'status',
        field: 'statusLabel',
        chipLabel: 'Status',
        order: ['Active', 'Disabled'],
    },
    { key: 'trunk', field: 'trunkName', chipLabel: 'Trunk' },
    {
        key: 'callingProfile',
        field: 'callingProfileName',
        chipLabel: 'Profile',
    },
    { key: 'usage', field: 'usageName', chipLabel: 'Usage' },
];

export const isOverstampDifferent = (row) =>
    !!row.trunkID && !!row.overstamp && row.overstamp !== row.e164;

/** A row's value for a set filter column, with blanks as NONE_VALUE. */
export const getSetFilterValue = (row, field) => row?.[field] || NONE_VALUE;

export const getValueLabel = (value) => (value === NONE_VALUE ? 'None' : value);

/**
 * How many rows have each value, per attribute filter, for the counts in the
 * header filter lists. Counted over every row (not the filtered list) so the
 * counts stay put while other filters change.
 *
 * @returns {Object<string, Map<string, number>>} keyed by column id
 */
export const getFilterCounts = (rows) =>
    Object.fromEntries(
        ATTRIBUTE_FILTERS.map(({ key, field }) => {
            const counts = new Map();
            rows.forEach((row) => {
                const value = getSetFilterValue(row, field);
                counts.set(value, (counts.get(value) ?? 0) + 1);
            });
            return [key, counts];
        }),
    );

/**
 * Sort order for a set filter's values: the listed values first, then the
 * rest alphabetically, with None last.
 */
export const compareFilterValues =
    (order = []) =>
    (a, b) => {
        const rank = (value) => {
            if (value === NONE_VALUE) return order.length + 1;
            const index = order.indexOf(value);
            return index === -1 ? order.length : index;
        };
        return (
            rank(a) - rank(b) ||
            getValueLabel(a).localeCompare(getValueLabel(b))
        );
    };

/**
 * (3286) AG Grid external filter that hides numbers that can't be ticked
 * unless Show ineligible is on. The grid keeps the callbacks it first gets,
 * so the switch is read through a ref.
 */
export const makeIneligibleFilter = (showIneligibleRef) => ({
    isExternalFilterPresent: () => !showIneligibleRef.current,
    doesExternalFilterPass: (node) => !node.data?.unselectableReason,
});

/**
 * How many ineligible numbers Show ineligible is hiding right now: those that
 * match the search and every column filter. AG Grid applies the switch before
 * the column filters, so they are re-checked here with the grid's own filter
 * values and options.
 *
 * @param {import('ag-grid-community').GridApi} api
 * @param {string} search the search box text
 */
export const countHiddenIneligible = (api, search) => {
    const words = parseNumberSearch(search);
    const columnChecks = Object.entries(api.getFilterModel() ?? {}).map(
        ([colId, model]) => {
            const colDef = api.getColumnDef(colId);
            const valueOf = (data) => colDef?.filterValueGetter?.({ data });
            if (model.filterType === 'set') {
                const values = new Set(model.values);
                return (data) => values.has(valueOf(data));
            }
            const option = colDef?.filterParams?.filterOptions?.find(
                ({ displayKey }) => displayKey === model.type,
            );
            return (data) =>
                !option?.predicate ||
                option.predicate([model.filter, model.filterTo], valueOf(data));
        },
    );

    let count = 0;
    api.forEachNode(({ data }) => {
        if (
            data?.unselectableReason &&
            words.every((word) => data.digits.includes(word)) &&
            columnChecks.every((check) => check(data))
        ) {
            count += 1;
        }
    });
    return count;
};

/** Quick filter parser: the search box matches digits only. */
export const parseNumberSearch = (text) => {
    const digits = digitsOnly(text);
    return digits ? [digits] : [];
};

// Number pattern modes, offered as custom options of the Number column's
// native text filter.
export const REGEX_MODES = [
    { value: 'numContains', label: 'Contains' },
    { value: 'numStartsWith', label: 'Starts with' },
    { value: 'numEndsWith', label: 'Ends with' },
    { value: 'numNotContains', label: 'Does not contain' },
    { value: 'numLength', label: 'Length is exactly' },
    { value: 'numLengthBetween', label: 'Length between' },
    { value: 'numRegex', label: 'Custom regex' },
];

/**
 * Builds the pattern for a number-pattern mode. Every mode except Custom
 * works on digits only, matching how numbers are compared (digits, no +).
 *
 * @returns {string} the regex source, or '' when there is nothing to apply
 */
export const buildRegexSource = ({ mode, value = '', max = '' }) => {
    const digits = digitsOnly(value);
    const maxDigits = digitsOnly(max);

    switch (mode) {
        case 'numContains':
            return digits;
        case 'numStartsWith':
            return digits && `^${digits}`;
        case 'numEndsWith':
            return digits && `${digits}$`;
        case 'numNotContains':
            return digits && `^(?!.*${digits}).*$`;
        case 'numLength':
            return digits && `^\\d{${digits}}$`;
        case 'numLengthBetween':
            return digits && maxDigits ? `^\\d{${digits},${maxDigits}}$` : '';
        case 'numRegex':
            return value.trim();
        default:
            return '';
    }
};

/** @returns {RegExp|null} null when the source is empty or invalid */
export const compileRegex = (source) => {
    if (!source) return null;
    try {
        return new RegExp(source);
    } catch {
        return null;
    }
};

/**
 * Custom options for the Number column's text filter. The column's filter
 * value is the number's digits, and an empty or invalid pattern hides
 * nothing.
 */
export const NUMBER_FILTER_OPTIONS = REGEX_MODES.map(
    ({ value: mode, label }) => ({
        displayKey: mode,
        displayName: label,
        numberOfInputs: mode === 'numLengthBetween' ? 2 : 1,
        predicate: ([value, max], cellValue) => {
            const regex = compileRegex(buildRegexSource({ mode, value, max }));
            return regex ? regex.test(String(cellValue ?? '')) : true;
        },
    }),
);

const REGEX_LABEL_MAX = 26;

export const getRegexLabel = ({ mode, value = '', max = '' }) => {
    const modeLabel = REGEX_MODES.find(
        (option) => option.value === mode,
    )?.label;
    const valueLabel =
        mode === 'numLengthBetween' ? `${value}–${max}` : value.trim();
    const label = `${modeLabel}: ${valueLabel}`;
    return label.length > REGEX_LABEL_MAX
        ? `${label.slice(0, REGEX_LABEL_MAX - 1)}…`
        : label;
};

/** The toolbar's filters as an AG Grid filter model. */
export const toFilterModel = (filters) => {
    const model = {};
    ATTRIBUTE_FILTERS.forEach(({ key }) => {
        if (filters[key]?.length) {
            model[key] = { filterType: 'set', values: filters[key] };
        }
    });
    if (filters.overstampDiffers) {
        model[FILTER_COLUMNS.overstampDiffers] = {
            filterType: 'set',
            values: [OVERSTAMP_DIFFERS_VALUE],
        };
    }
    if (filters.paste) {
        model[FILTER_COLUMNS.paste] = {
            filterType: 'set',
            values: filters.paste.digits,
        };
    }
    if (filters.regex) {
        const { mode, value, max } = filters.regex;
        model[FILTER_COLUMNS.number] = {
            filterType: 'text',
            type: mode,
            filter: value,
            ...(max ? { filterTo: max } : {}),
        };
    }
    return model;
};

/**
 * Whether anything is narrowing All Numbers: the search box, a pasted list,
 * a pattern or any column filter.
 */
export const hasActiveFilters = (filters) =>
    filters.search !== '' || Object.keys(toFilterModel(filters)).length > 0;

/**
 * The toolbar's filters read back from AG Grid's filter model, so changes made
 * in a column header show in the toolbar too. The search text and the pasted
 * list's total aren't part of the model, so they come from the caller.
 */
export const fromFilterModel = (model, { search = '', pasteTotal } = {}) => {
    const filters = { ...EMPTY_FILTERS, search };
    const current = model ?? {};

    ATTRIBUTE_FILTERS.forEach(({ key }) => {
        filters[key] = current[key]?.values ?? [];
    });
    filters.overstampDiffers = !!current[
        FILTER_COLUMNS.overstampDiffers
    ]?.values?.includes(OVERSTAMP_DIFFERS_VALUE);

    const paste = current[FILTER_COLUMNS.paste];
    if (paste) {
        filters.paste = {
            digits: paste.values,
            total: pasteTotal ?? paste.values.length,
        };
    }

    const number = current[FILTER_COLUMNS.number];
    if (number?.type) {
        const { type: mode, filter: value = '', filterTo: max = '' } = number;
        filters.regex = {
            mode,
            value,
            max,
            label: getRegexLabel({ mode, value, max }),
        };
    }
    return filters;
};

const listLabel = (values) => values.map(getValueLabel).join(', ');

/**
 * The "Filtering by" chips, in toolbar order.
 *
 * @returns {{ key: string, label: string }[]}
 */
export const getActiveFilterChips = (filters) => {
    const chips = [];
    if (digitsOnly(filters.search)) {
        chips.push({
            key: 'search',
            label: `Number: ${filters.search.trim()}`,
        });
    }
    ATTRIBUTE_FILTERS.forEach(({ key, chipLabel }) => {
        if (filters[key]?.length) {
            chips.push({
                key,
                label: `${chipLabel}: ${listLabel(filters[key])}`,
            });
        }
    });
    if (filters.overstampDiffers) {
        chips.push({ key: 'overstampDiffers', label: 'Overstamp differs' });
    }
    if (filters.paste) {
        chips.push({
            key: 'paste',
            label: `Pasted list: ${filters.paste.digits.length} (of ${filters.paste.total})`,
        });
    }
    if (filters.regex) {
        chips.push({ key: 'regex', label: filters.regex.label });
    }
    return chips;
};

export const clearFilter = (filters, key) => ({
    ...filters,
    [key]: EMPTY_FILTERS[key],
});
